import React, { useState, useEffect } from "react";
import {
  FaTrash,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaEdit,
} from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../../creds";
import { toast } from "react-toastify";
import PaginationModule from "./pagination/PaginationModule"
import TaskViewModal from "./TaskViewModel";

const TasksView = ({ onEditTask }) => {
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [perPage] = useState(10);
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);
  const access_token = localStorage.getItem('access_token');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [totalTasks, setTotalTasks] = useState(10);
  const [isTaskViewModalOpen, setIsTaskViewModalOpen] = useState(false);
  const [selectedCRNItem, setSelectedCRNItem] = useState(null);

 
  const handleCRNClick = (item) => {
    setSelectedCRNItem(item);
    setIsTaskViewModalOpen(true);
  };

  const handleCloseCRNSchemaModal = () => {
    setIsTaskViewModalOpen(false);
    setSelectedCRNItem(null);
  };

  // 1. FETCH TASKS – With Toast Feedback
  const fetchData = async (page = 1, postsPerPage = 10) => {
    setLoading(true);
    // setError(null);
  
    try {
      const response = await axios.get(
        `${API_URL}/upload_tasks?page=${page}&size=${postsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
  
      // Your API returns: { tasks: [...], totalPages: number }
      const { tasks = [], totalPages = 1, totalTasks } = response.data || {};
  
      // Update state
      setData(tasks);
      setTotalPages(totalPages);
      setTotalTasks(totalTasks);

      setCurrentPage(page); 
  
      // User feedback
      if (tasks.length === 0) {
        toast.info("No stories yet. Create your first translation!", {
          icon: "Rocket",
          autoClose: 3000,
        });
      } else {
        const storyWord = tasks.length === 1 ? "story" : "stories";
        toast.success(`Loaded ${tasks.length} ${storyWord} (Page ${page})`, {
          autoClose: 2000,
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
  
      let errorMessage = "Failed to load stories";
  
      if (error.response) {
        // Server responded with error status
        errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        // No response received (network issue)
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message;
      }
  
      // setError(errorMessage);
      toast.error(errorMessage, { autoClose: 5000 });
  
      // Reset on error
      setData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData(currentPage, postsPerPage);
    // eslint-disable-next-line
  }, [currentPage, postsPerPage]);


  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    setData([...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    }));
  };

  const handleDownload = async (taskId) => {
    if (!taskId) {
      toast.error("Invalid task ID");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/download_translation/${taskId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${access_token}`,
          // Don't set Content-Type for file download — let browser handle it
        },
      });

      // SUCCESS: File found
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `${taskId}_translation.txt`; // fallback

        // Try to get filename from backend (if sent)
        if (contentDisposition && contentDisposition.includes("filename=")) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }

        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`Downloaded: ${filename}`, {
          icon: "Download complete",
          autoClose: 3000,
        });
        return;
      }

      // ERROR: Server returned error (404, 401, etc.)
      let errorMessage = "Download failed";

      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        // If JSON parse fails (e.g. HTML error page)
        if (response.status === 404) errorMessage = "Translation file not found";
        if (response.status === 401) errorMessage = "Session expired. Please log in again";
        if (response.status === 500) errorMessage = "Server error. Try again later";
      }

      // Show proper toast error
      if (response.status === 404) {
        toast.warning("No translation file found yet");
      } else if (response.status === 401) {
        toast.error("Session expired – please log in again");
      } else {
        toast.error(errorMessage);
      }

    } catch (err) {
      console.error("Download error:", err);
      toast.error("Network error – check your connection");
    }
  };


  const handleEdit = async (task) => {
    setLoading(true);
    // setError(null);
  
    try {
      const response = await axios.get(`${API_URL}/get_task/${task.story_id}`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
  
      const data = response.data;
  
      // Check if essential fields exist
      if (
        data.source_text == null ||
        data.target_text == null ||
        data.source_language == null ||
        data.target_language == null
      ) {
        throw new Error("Incomplete task data – files may be missing");
      }
  
      const formattedTask = {
        story_id: task.story_id,
        source_language: data.source_language || "en",
        target_language: data.target_language || "ur",
        source_text: data.source_text || "",
        target_text: data.target_text || "",
        latest_version: data.latest_version || 1,
        is_edited: data.is_edited || false,
      };
  
      onEditTask(formattedTask);
      return;
  
    } catch (error) {
      console.error("Edit load error:", error);
  
      let userMessage = "Failed to load task for editing.";
      let isFilesMissing = false;
  
      if (error.response) {
        const { status, data: serverData } = error.response;
        const serverMsg = serverData?.error || serverData?.message || "";
  
        if (status === 404) {
          // ── Most common case: files deleted but record still in DB ──
          if (
            serverMsg.toLowerCase().includes("not found") ||
            serverMsg.toLowerCase().includes("folder") ||
            serverMsg.toLowerCase().includes("directory") ||
            serverMsg.toLowerCase().includes("file") ||
            serverMsg.toLowerCase().includes("storage") ||
            serverMsg.includes("does not exist")
          ) {
            userMessage = "Translation files for this task are missing or have been deleted.";
            isFilesMissing = true;
          } else {
            userMessage = "This task no longer exists in the system.";
          }
        } 
        else if (status === 401 || status === 403) {
          userMessage = "Session expired or unauthorized. Please log in again.";
        } 
        else if (status === 500) {
          userMessage = "Server encountered an unexpected error. Please try again later.";
        } 
        else if (serverMsg) {
          userMessage = serverMsg;
        }
      } 
      else if (error.request) {
        userMessage = "No response from server. Please check your internet connection.";
      } 
      else if (error.message?.includes("Incomplete task data")) {
        userMessage = "Task associated files may be missing.";
        isFilesMissing = true;
      }
  
      // Special styling / longer message for missing files case
      toast.error(
        <div>
          <strong>{userMessage}</strong>
          {isFilesMissing && (
            <p className="text-sm mt-1 opacity-90">
              The task still shows in the list, but the actual translation content could not be found.
              Contact Adminstrator if this task is important.
            </p>
          )}
        </div>,
        {
          position: "top-center",
          autoClose: 8000,
      });
  
      // setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  // 2. DELETE STORY – With Toast + Auto Refresh
  const handleDelete = async (task) => {
    // Beautiful custom confirm toast
    toast(
      ({ closeToast }) => (
        <div className="p-4">
          <p className="font-medium mb-4">
            Delete <span className="text-red-600">"{task.story_title || "Untitled"}"</span>?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={closeToast}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                closeToast();
                await performDelete(task);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      }
    );
  };

  // Actual delete function
  const performDelete = async (task) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/delete_story/${task.story_id}`,
        {},
        {
          headers: { Authorization: `Bearer ${access_token}` },
        }
      );

      toast.success(res.data.message || "Story deleted successfully!", {
        icon: "Deleted",
      });

      // Auto-refresh the list
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to delete story";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    { key: "index", label: "SN", className: "w-[3%]" },
    { key: "story_id", label: "Story ID", className: "w-[8%]" },
    // { key: "pageno", label: "Page No", className: "w-[8%]" },
    // { key: "userid", label: "User ID", className: "w-[8%]" },
    { key: "story_title", label: "Story Title (Source)", className: "w-[25%]" },
    { key: "created_at", label: "Date", className: "w-[6%]" },
    { key: "actions", label: "Actions", className: "w-[15%] text-center" },
  ];

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <FaArrowUp className="inline text-gray-900 ml-1" />
    ) : (
      <FaArrowDown className="inline text-gray-900 ml-1" />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return "—";
  
    // Remove timezone part if present (GMT, UTC, etc.)
    const cleaned = dateStr.replace(/\s*(GMT|UTC|Z|\+|-)\d*.*/i, '').trim();
  
    // Expected pattern: "Wed, 14 Jan 2026 17:22:06"
    const regex = /^(\w+),\s*(\d{1,2})\s*(\w+)\s*(\d{4})/;
    const match = cleaned.match(regex);
  
    if (!match) {
      // Fallback: try native Date parsing
      const dt = new Date(dateStr);
      if (!isNaN(dt.getTime())) {
        const d = String(dt.getDate()).padStart(2, '0');
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const y = dt.getFullYear();
        return `${d}-${m}-${y}`;
      }
      return "—";
    }
  
    const [, , day, monthStr, year] = match;
  
    // Month name → number
    const monthMap = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };
  
    const month = monthMap[monthStr];
    if (!month) return "—";
  
    return `${day.padStart(2, '0')}-${month}-${year}`;
  };


  return (
    <div className="w-full min-h-screen flex justify-center">
      <div className="w-full max-w-6xl mx-auto p-4">
        {/* {loading && <div className="text-center">Loading...</div>} */}
        {/* {error && <div className="text-center text-red-500">{error}</div>} */}
        <table className="w-full bg-white border border-gray-300 shadow-md rounded-lg table-fixed">
          <thead>
            <tr className="bg-gray-200 text-gray-900 text-left font-semibold text-[13px] uppercase tracking-wide">
              {headers.map(({ key, label, className }) => (
                <th
                  key={key}
                  className={`px-3 py-2 border border-gray-300 ${className} ${key !== "actions" && key !== "index"
                    ? "cursor-pointer hover:bg-gray-300 transition-colors duration-150"
                    : ""
                    }`}
                  onClick={() =>
                    key !== "actions" && key !== "index" && handleSort(key)
                  }
                >
                  <div className="flex items-center">
                    <span>{label}</span>
                    {key !== "actions" && key !== "index" && getSortIcon(key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => (
                <tr
                  key={item.story_id}
                  className="border-b hover:bg-gray-50 text-[12px]"
                >
                  <td className="py-2 px-3 border border-gray-300 text-center">
                    {(currentPage - 1) * perPage + index + 1}
                  </td>
                  <td className="py-2 px-3 border border-gray-300 cursor-pointer hover:text-blue-600" onClick={() => handleCRNClick(item)}>
                    {item.story_id
                      ? String(item.story_id).slice(0, 4) + "-" +
                      String(item.story_id).slice(4, 8) + "-" +
                      String(item.story_id).slice(8, 12) + "-" +
                      String(item.story_id).slice(12)
                      : '-'}

                  </td>
                  {/* <td className="py-2 px-3 border border-gray-300">
                    {item.pageno}
                  </td>
                  <td className="py-2 px-3 border border-gray-300">
                    {item.userid}
                  </td> */}
                  <td
                    className={`py-2 px-3 border border-gray-300 break-normal text-justify ${item.srcLang === "ur" ? "text-right" : "text-left"
                      }`}
                    dir={item.srcLang === "ur" ? "rtl" : "ltr"}
                  >
                    {item.story_title || "-"}
                  </td>
                  <td className="py-2 px-3 border border-gray-300">
                    {formatDate(item.created_at)}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-white bg-yellow-500 hover:bg-yellow-600 p-1 rounded"
                        title="Edit Translation"
                        disabled={loading}
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => handleDownload(item.story_id)}
                        className="text-white bg-green-500 hover:bg-green-600 p-1 rounded"
                        title="Download"
                        disabled={loading}
                      >
                        <FaDownload size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-white bg-red-600 hover:bg-red-700 p-1 rounded"
                        title="Delete"
                        disabled={loading}
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className="py-6 px-4 text-center text-gray-500 text-sm"
                >
                  No tasks uploaded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <PaginationModule totalPages={totalPages} totalTasks={totalTasks} postsPerPage={postsPerPage} setPostsPerPage={setPostsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage}  />
      </div>

      <TaskViewModal 
      isOpen={isTaskViewModalOpen}
      onClose={handleCloseCRNSchemaModal}
      item={selectedCRNItem}
      />

    </div>
  );
};

export default TasksView;
